/* eslint-disable react/jsx-pascal-case */
import React, { memo, useMemo } from 'react';
import { bool, shape, string, arrayOf } from 'prop-types';
import classNames from 'classnames/bind';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { AppContext } from '@thd-nucleus/app-render';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { declareContexts } from '@thd-olt-component-react/hydrator';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  getContentfulTargetingIds,
  useVariationIds,
} from '@thd-olt-component-react/personalization-utils';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { SponsoredUniversalBanner } from '@thd-olt-component-react/sponsored-content';
import { componentMapper } from '../dataModel/componentMapper';
import { parseContent, parseSponsoredContent } from '../utils/utilities';
import { useGetUrlParameter } from '../hooks/useGetUrlParameter';
import { EducationalRenderResolver } from './components/EducationalRenderResolver';
import styles from './educational-layout.scss';
import { previewComponentMapper } from '../dataModel/previewComponentMapper';

/**
 * Main job of this component is to just render the various harmony components only when necessary (see react pure
 * components).
 */
export const EducationalLayoutRenderer = memo(({
  canonicalURL,
  data,
  filterBreadcrumbs,
  pageId,
  customerType,
  storeId,
  pageTypeComponents,
  search,
  thdSEOBotDetection,
  isPreviewLink,
  isLivePreview = false
}) => {

  declareContexts([AppContext, CustomerContext]);
  const thdCustomer = useThdCustomer();
  const cx = classNames.bind(styles);
  const layouts = data?.layouts;
  const content = layouts?.content;
  const experienceHasSideNav = !!content?.sideNavigation?.id;

  const { mboxes, innervateTags } = getContentfulTargetingIds({
    content,
    containerKeys: {
      SpotlightContainer: 'spotlightList'
    }
  });

  const isReady = mboxes.length > 0 || innervateTags.length > 0;

  // adobe's Campaign Management Multi-Media Channel parameter to be passed to Citi, so that we can
  // track credit card campaigns through application completions - for Spotlight and Hero components
  const cmMmcParam = useGetUrlParameter('cm_mmc');

  const { variationIds, renderDefault } = useVariationIds({
    mboxes,
    innervateTags,
    isReady,
  });

  const theme = useTheme(EducationalLayoutRenderer);
  const { hideInternalMarketingBanners } = theme.props;

  // This logic appends '_b2b' to the pageId for B2B customer types so the EndCap component can render for both B2B and B2C at the same time
  const endCapPageUrl = customerType.toLowerCase() === 'b2b'
    ? `${pageId}_b2b`
    : pageId;

  const customProps = {
    ComponentSelector: (component) => ({
      componentMap: isLivePreview ? previewComponentMapper.getComponentMap() : componentMapper.getComponentMap(),
      defaultComponent: component.defaultComponent,
      variations: component.variations,
      variationIds: variationIds || [],
      renderDefault,
      isSeoBot: thdSEOBotDetection,
      targetingType: component.targetingType,
      customPropMap: {
        Spotlight: {
          showDescription: true,
          cmMmcParam
        },
        SpotlightContainer: {
          showDescription: true,
          cmMmcParam
        },
        HeroPersonalized: {
          thdPageId: pageId,
          searchParams: search,
          isSeoBot: thdSEOBotDetection,
          customerType
        },
        Hero: {
          hasSidenav: true,
          cmMmcParam
        }
      }
    }),
    EndCap: () => ({
      customerType: customerType.toUpperCase(),
      pageUrl: `/${endCapPageUrl}`
    }),
    SeoLinks: () => ({
      path: canonicalURL
    }),
    Spotlight: () => ({
      showDescription: true,
      cmMmcParam,
      shouldUseContentPlayer: true
    }),
    SpotlightContainer: () => ({
      showDescription: true,
      cmMmcParam,
      variationIds: variationIds || [],
      renderDefault,
      isSeoBot: thdSEOBotDetection,
    }),
    HeroPersonalized: () => ({
      thdPageId: pageId,
      searchParams: search,
      isSeoBot: thdSEOBotDetection,
      customerType
    }),
    Hero: () => ({
      cmMmcParam
    }),
    Resource: () => ({
      shouldUseContentPlayer: true
    }),
    PromoVisualNavigation: () => ({
      experienceHasSideNav: true,
      enableTheme: true
    }),
    ProductShelf: () => ({
      showTooFewProductsNotice: isPreviewLink
    }),
    VisualNavigation: () => ({
      enableTheme: true
    }),
  };

  const { Content } = useMemo(() => parseContent({
    data,
    componentMapper: isLivePreview ? previewComponentMapper : componentMapper,
    customProps,
    layout: 'educational',
  }), [data, variationIds, renderDefault, search]);

  const { Sponsored } = useMemo(() => parseSponsoredContent({
    data,
    componentMapper: isLivePreview ? previewComponentMapper : componentMapper,
    customProps,
    layout: 'educational',
  }), [data]);

  const pageTitle = content?.title;
  const descriptionText = content?.descriptionNew?.descriptionText;
  const descriptionRichText = content?.descriptionNew?.description;
  const descriptionTitle = content?.descriptionNew?.title || '';
  const bottomCarouselCustomRenderId = content?.sponsoredBottomCarousel?.customRenderId;
  const middleBannerCustomRenderId = content?.sponsoredMiddleBanner?.customRenderId;
  const skyscraperBannerCustomRenderId = content?.sponsoredSkyscraperBanner?.customRenderId;
  const topBannerCustomRenderId = content?.sponsoredTopBanner?.customRenderId;
  const sponsoredBrowserId = thdCustomer?.mcvisID || '';
  const isCategoryPage = layouts?.type === 'Category';

  let contentfulDefaultBanner = {};

  if (content?.sponsoredMiddleBanner !== null) {
    contentfulDefaultBanner = content?.sponsoredMiddleBanner;
  } else if (content?.sponsoredTopBanner !== null) {
    contentfulDefaultBanner = content?.sponsoredTopBanner;
  }

  const { landingPageBreadcrumbs, landingPageMetadata } = pageTypeComponents;

  return (
    <Row name="educational-layout-manager">
      <ErrorBoundary name="metadata">
        {landingPageMetadata}
      </ErrorBoundary>
      <Row className="isBound">
        <Col xs="12" className={cx('educational-layout__no-bottom-padding')}>
          <ErrorBoundary name="breadcrumbs">
            {landingPageBreadcrumbs}
          </ErrorBoundary>
        </Col>
        <Col xs="12" className={cx('educational-layout__no-top-bottom-padding')}>
          <ErrorBoundary name="sponsored-top-banner">
            <Sponsored.sponsoredTopBanner
              browserId={sponsoredBrowserId}
              slugId={pageId}
              customRenderId={topBannerCustomRenderId}
              contentfulDefaultBanner={contentfulDefaultBanner}
              schema="cat_sponsored"
            />
          </ErrorBoundary>
        </Col>
        <Col xs="12">
          {pageTitle && (
            <ErrorBoundary name="heading">
              <Typography
                variant="h1"
                weight="display"
                height="snug"
                color="primary"
                uppercase
                data-contentful-entry-id={layouts?.content?.id}
                data-contentful-field-id="title"
              >
                {pageTitle}
              </Typography>
            </ErrorBoundary>
          )}
          <Row className={`isBound ${!experienceHasSideNav && ' sui-justify-center'}`}>
            {experienceHasSideNav && (
              <Col sm="2" md="2" lg="2">
                <ErrorBoundary name="side-navigation">
                  <div className={cx('educational-layout__side-navigation')}>
                    <Content.sideNavigation />
                  </div>
                </ErrorBoundary>
                {isCategoryPage && (
                  <ErrorBoundary name="sponsored-skyscraper-banner">
                    <Sponsored.sponsoredSkyscraperBanner
                      browserId={sponsoredBrowserId}
                      slugId={pageId}
                      customRenderId={skyscraperBannerCustomRenderId}
                      schema="cat_sponsored"
                    />
                  </ErrorBoundary>
                )}
              </Col>
            )}
            <Col xs="12" sm="10" md="10" lg="10">
              <ErrorBoundary name="hero-carousel">
                <Content.heroCarousel />
              </ErrorBoundary>
              <EducationalRenderResolver componentId="promo-visual-navigation-1">
                <Content.promoVisualNavigation1 />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="promo-visual-navigation-2">
                <Content.promoVisualNavigation2 />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="promo-visual-navigation-3">
                <Content.promoVisualNavigation3 />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="endcap">
                <Content.endCap />
              </EducationalRenderResolver>
              {!hideInternalMarketingBanners && (
                <EducationalRenderResolver componentId="internal-marketing-banner-1">
                  <Content.internalMarketingBanner1 />
                </EducationalRenderResolver>
              )}
              <EducationalRenderResolver componentId="spotlight-section">
                <Content.spotlightSection slidesLg={2} slidesXl={3} slidesMd={2} slidesSm={1} />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="sponsored-middle-banner">
                <Sponsored.sponsoredMiddleBanner
                  browserId={sponsoredBrowserId}
                  slugId={pageId}
                  customRenderId={middleBannerCustomRenderId}
                  contentfulDefaultBanner={contentfulDefaultBanner}
                  schema="cat_sponsored"
                />
              </EducationalRenderResolver>
              {!hideInternalMarketingBanners && (
                <EducationalRenderResolver componentId="internal-marketing-banner-2">
                  <Content.internalMarketingBanner2 />
                </EducationalRenderResolver>
              )}
              <EducationalRenderResolver componentId="visual-navigation-1">
                <Content.visualNavigation1 />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="visual-navigation-2">
                <Content.visualNavigation2 />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="visual-navigation-3">
                <Content.visualNavigation3 />
              </EducationalRenderResolver>
              {!hideInternalMarketingBanners && (
                <EducationalRenderResolver componentId="internal-marketing-banner-3">
                  <Content.internalMarketingBanner3 />
                </EducationalRenderResolver>
              )}
              <EducationalRenderResolver componentId="special-buy-banner">
                <Content.specialBuyOfTheDay specialBuyType="sbotd" />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="product-shelf">
                <Content.productShelf />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="resource">
                <Content.resourcesSection />
              </EducationalRenderResolver>
              {isCategoryPage && (
                <EducationalRenderResolver componentId="sponsored-leaderboard-banner">
                  <SponsoredUniversalBanner
                    bannerType="leaderboard"
                    nValue={pageId}
                    schema="cat_sponsored"
                  />
                </EducationalRenderResolver>
              )}
              {!hideInternalMarketingBanners && (
                <EducationalRenderResolver componentId="medio-inline-1">
                  <Content.medioInline1 />
                </EducationalRenderResolver>
              )}
              {!hideInternalMarketingBanners && (
                <EducationalRenderResolver componentId="piccola-pencil-1">
                  <Content.piccolaPencil1 />
                </EducationalRenderResolver>
              )}
              {Content.dynamicSections?.map((SubSection, key) => (
                <EducationalRenderResolver
                  componentId={`dynamic-section-${key}`}
                  key={`dynamic-section-${key}`}
                >
                  <SubSection />
                </EducationalRenderResolver>
              ))}
              {!hideInternalMarketingBanners && (
                <EducationalRenderResolver componentId="medio-inline-2">
                  <Content.medioInline2 />
                </EducationalRenderResolver>
              )}
              {!hideInternalMarketingBanners && (
                <EducationalRenderResolver componentId="piccola-pencil-2">
                  <Content.piccolaPencil2 />
                </EducationalRenderResolver>
              )}
              <EducationalRenderResolver componentId="sponsored-bottom-carousel">
                <Sponsored.sponsoredBottomCarousel
                  plaLocation="sponsoredCarouselLandingPage"
                  browserId={sponsoredBrowserId}
                  breadCrumbs={filterBreadcrumbs}
                  customRenderId={bottomCarouselCustomRenderId}
                  pageContext={{
                    schema: 'cat_sponsored'
                  }}
                  nValue={pageId}
                />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="description">
                <Content.descriptionNew title={descriptionTitle} description={descriptionText} descriptionRichText={descriptionRichText} />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="related-search">
                <Content.relatedSearchesAndProducts navParam={pageId} />
              </EducationalRenderResolver>
              <EducationalRenderResolver componentId="seo-links">
                {canonicalURL && <Content.seoLinks />}
              </EducationalRenderResolver>
            </Col>
          </Row>
        </Col>
      </Row>
    </Row>
  );
});

EducationalLayoutRenderer.displayName = 'EducationalLayoutRenderer';

EducationalLayoutRenderer.propTypes = {
  canonicalURL: string,
  data: shape({}).isRequired,
  filterBreadcrumbs: arrayOf(shape(
    {
      browseUrl: string,
      label: string,
    },
  )),
  pageId: string.isRequired,
  storeId: string.isRequired,
  customerType: string.isRequired,
  pageTypeComponents: shape({}).isRequired,
  search: string,
  thdSEOBotDetection: bool,
  isPreviewLink: bool,
  isLivePreview: bool
};

EducationalLayoutRenderer.defaultProps = {
  canonicalURL: null,
  filterBreadcrumbs: null,
  search: '',
  thdSEOBotDetection: false,
  isPreviewLink: false
};

EducationalLayoutRenderer.themeProps = {
  hideInternalMarketingBanners: bool,
};

EducationalLayoutRenderer.defaultThemeProps = {
  hideInternalMarketingBanners: false
};
